import {
  DSCheckbox,
  DSInput,
} from "@components/design-system/molecules";
import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mui/material";
import { useState } from "react";

import {
  AccountTypeDropdown,
  RoleDropdown,
} from "../../TeamManagement/EditExistingTeamMembers";

const AddUsersTable = ({
  users = [],
  isExisting,
  setUsers,
  updateUsersAndBillingText,
  error,
  orgAllowsDirectMemberAdd,
  orgIsManualBilling,
  hasBillingSetup,
  onboardingMode = false,
}) => {
  const [originalAccountTypes] = useState(() =>
    users.reduce((acc, user) => {
      acc[user.email] = user.accountType;
      return acc;
    }, {}),
  );

  const handleInputChange = (e, index, field) => {
    const value = e?.target?.value;
    setUsers((prevValues) => {
      const newValues = [...prevValues];
      newValues[index] = {
        ...newValues[index],
        [field]: value,
      };
      return newValues;
    });
    if (typeof updateUsersAndBillingText === "function") {
      updateUsersAndBillingText();
    }
  };

  const handleCheckboxChange = (e, index) => {
    const checked = e.target.checked;
    setUsers((prevValues) => {
      const newValues = [...prevValues];
      newValues[index] = {
        ...newValues[index],
        isAdmin: checked,
      };
      return newValues;
    });
  };

  const handleRemove = (index) => {
    setUsers((prevValues) => {
      const newValues = [...prevValues];
      newValues.splice(index, 1);
      return newValues;
    });
  };

  const columnWidthSmall = !orgIsManualBilling ? "w-1/5" : "w-1/4";
  const columnWidthLarge = !orgIsManualBilling ? "w-1/4" : "w-1/3";

  return (
    <table
      className={`table-fixed w-full ${onboardingMode ? "text-white dark:text-white" : "text-primary"} text-left`}
    >
      <thead>
        <tr className="border-b border-primary">
          <th className={`font-semibold text-sm ${columnWidthLarge}`}>
            Email
          </th>
          {orgAllowsDirectMemberAdd && (
            <>
              <th
                className={`font-semibold text-sm ${columnWidthLarge}`}
              >
                First Name
              </th>
              <th
                className={`font-semibold text-sm ${columnWidthLarge}`}
              >
                Last Name
              </th>
            </>
          )}
          <th className={`font-semibold text-sm ${columnWidthLarge}`}>
            Role
          </th>

          <th className={`font-semibold text-sm ${columnWidthSmall}`}>
            {!hasBillingSetup ? (
              <Tooltip
                arrow
                placement="right"
                title={
                  "Set up billing to modify the account plan of your users"
                }
              >
                <div>Account Plan</div>
              </Tooltip>
            ) : (
              <>Account Plan</>
            )}
          </th>
          <th
            className={`font-semibold text-sm ${columnWidthSmall} text-center`}
          >
            Team Admin
          </th>
          <th className="w-[30px]"></th>
        </tr>
      </thead>
      <tbody>
        {users?.map((user, index) => (
          <tr key={index}>
            <td className={`${columnWidthLarge} px-1 py-1  min-w-0`}>
              <p
                className={`w-full pr-2 truncate overflow-hidden text-ellipsis text-sm bg-transparent ${onboardingMode ? "text-white" : "text-primary"}`}
              >
                {user?.email ?? ""}
              </p>
            </td>
            {orgAllowsDirectMemberAdd && (
              <>
                <td
                  className={`${columnWidthLarge} px-1 py-1 truncate overflow-hidden text-ellipsis min-w-0`}
                >
                  {isExisting ? (
                    <p
                      className={`w-full text-sm bg-transparent ${onboardingMode ? "text-white" : "text-primary"}`}
                    >
                      {user?.firstName ?? ""}
                    </p>
                  ) : (
                    <DSInput
                      type="text"
                      defaultValue={user?.firstName ?? ""}
                      dataTestId="first-name-input"
                      error={
                        error && !users[index]?.firstName
                          ? "Required"
                          : undefined
                      }
                      onChange={(e) =>
                        handleInputChange(e, index, "firstName")
                      }
                    />
                  )}
                </td>
                <td
                  className={`${columnWidthLarge} px-1 py-1 truncate overflow-hidden text-ellipsis min-w-0`}
                >
                  {isExisting ? (
                    <p
                      className={`w-full text-sm bg-transparent ${onboardingMode ? "text-white" : "text-primary"}`}
                    >
                      {user?.lastName ?? ""}
                    </p>
                  ) : (
                    <DSInput
                      type="text"
                      defaultValue={user?.lastName ?? ""}
                      dataTestId="last-name-input"
                      error={
                        error && !users[index]?.lastName
                          ? "Required"
                          : undefined
                      }
                      onChange={(e) =>
                        handleInputChange(e, index, "lastName")
                      }
                    />
                  )}
                </td>
              </>
            )}
            <td
              className={`${columnWidthSmall} relative px-1 py-1 truncate overflow-hidden text-ellipsis min-w-0`}
            >
              <RoleDropdown
                user={users[index]}
                onChange={(_, value) =>
                  handleInputChange(
                    { target: { value } },
                    index,
                    "roleId",
                  )
                }
                error={error}
              />
            </td>
            <td
              className={`${columnWidthSmall} relative px-1 py-1 truncate overflow-hidden text-ellipsis min-w-0`}
            >
              <Tooltip
                arrow
                placement="right"
                title={
                  originalAccountTypes[users[index]?.email] ===
                  "unlimited"
                    ? "You can't modify the plan of a user who is paying for themself"
                    : "Set up billing to modify the account plan of your users"
                }
                disableFocusListener={
                  originalAccountTypes[users[index]?.email] ===
                    "basic" && hasBillingSetup
                }
                disableHoverListener={
                  originalAccountTypes[users[index]?.email] ===
                    "basic" && hasBillingSetup
                }
                disableTouchListener={
                  originalAccountTypes[users[index]?.email] ===
                    "basic" && hasBillingSetup
                }
              >
                <div>
                  <AccountTypeDropdown
                    user={users[index]}
                    onChange={(_, value) =>
                      handleInputChange(
                        { target: { value } },
                        index,
                        "accountType",
                      )
                    }
                    disabled={
                      originalAccountTypes[users[index]?.email] ===
                        "unlimited" || !hasBillingSetup
                    }
                  />
                </div>
              </Tooltip>
            </td>
            <td className={`${columnWidthSmall} text-center px-1`}>
              <div className="flex flex-row items-center justify-center relative">
                <DSCheckbox
                  checked={users[index]?.isAdmin || false}
                  onChange={(e) => handleCheckboxChange(e, index)}
                  dataTestId="team-admin-checkbox"
                  aria-label="Team Admin"
                />
              </div>
            </td>
            <td className="w-1/20 text-center px-1">
              <FontAwesomeIcon
                icon={faMinusCircle}
                size="lg"
                className="text-danger hover:text-red-700 cursor-pointer"
                onClick={() => handleRemove(index)}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default AddUsersTable;
